import React, { useState, useRef, Fragment, lazy } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { AnimatedSection } from '../shared/AnimatedSection';
import { Sign } from '../../data/signs';
interface VistaDelNucleoProps {
  sign: Sign;
}
// Real preview images per sign. Empty arrays = placeholder cards.
const VISTAS: Record<string, string[]> = {
  aries: ['https://cdn.shopify.com/s/files/1/0661/3847/2542/files/ChatGPT_Image_13_may_2026_10_13_27_p.m..png?v=1778725185', 'https://cdn.shopify.com/s/files/1/0661/3847/2542/files/ChatGPT_Image_13_may_2026_10_13_33_p.m..png?v=1778725186', 'https://cdn.shopify.com/s/files/1/0661/3847/2542/files/ChatGPT_Image_13_may_2026_10_13_39_p.m..png?v=1778725186', 'https://cdn.shopify.com/s/files/1/0661/3847/2542/files/ChatGPT_Image_13_may_2026_10_13_45_p.m..png?v=1778725185']
};
// Build slides — at least 4 placeholders for signs without images yet.
function getSlides(sign: Sign): Array<{
  type: 'image' | 'placeholder';
  url?: string;
}> {
  const images = VISTAS[sign.id] || [];
  if (images.length > 0) {
    return images.map(url => ({
      type: 'image' as const,
      url
    }));
  }
  return Array.from({
    length: 4
  }, () => ({
    type: 'placeholder' as const
  }));
}
export function VistaDelNucleo({
  sign
}: VistaDelNucleoProps) {
  const slides = getSlides(sign);
  const [active, setActive] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const goTo = (idx: number) => {
    const next = (idx + slides.length) % slides.length;
    setActive(next);
    const el = scrollRef.current;
    if (el) {
      const card = el.children[next] as HTMLElement | undefined;
      if (card) {
        el.scrollTo({
          left: card.offsetLeft - el.clientWidth / 2 + card.clientWidth / 2,
          behavior: 'smooth'
        });
      }
    }
  };
  return <section className="relative py-24 px-4 md:px-6 overflow-hidden bg-cosmic-black" data-id="element-467">
      {/* Atmospheric backdrop */}
      <div className="absolute inset-0 pointer-events-none" data-id="element-468">
        <div className="absolute inset-0" style={{
        background: `radial-gradient(ellipse at 50% 30%, ${sign.colors.primary}18 0%, transparent 60%)`
      }} data-id="element-469" />
        <div className="absolute inset-0" style={{
        background: `linear-gradient(180deg, #050508 0%, ${sign.colors.primary}08 50%, #050508 100%)`
      }} data-id="element-470" />
      </div>

      {/* Floating embers */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden" data-id="element-471">
        {[...Array(14)].map((_, i) => <motion.div key={i} className="absolute w-[2px] h-[2px] rounded-full" style={{
        backgroundColor: sign.colors.primary,
        left: `${i * 7.7 % 100}%`,
        top: `${i * 11 % 100}%`,
        boxShadow: `0 0 8px ${sign.colors.primary}`
      }} animate={{
        y: [-20, -60, -20],
        opacity: [0.15, 0.6, 0.15]
      }} transition={{
        duration: 7 + i % 5,
        repeat: Infinity,
        ease: 'easeInOut',
        delay: i * 0.4
      }} data-id="element-472" />)}
      </div>

      <div className="relative container mx-auto max-w-7xl" data-id="element-473">
        <AnimatedSection data-id="element-474">
          <div className="text-center mb-4" data-id="element-475">
            <motion.div className="inline-flex items-center gap-3 mb-5" initial={{
            opacity: 0
          }} whileInView={{
            opacity: 1
          }} viewport={{
            once: true
          }} data-id="element-476">
              <div className="h-px w-12" style={{
              background: `linear-gradient(90deg, transparent, ${sign.colors.primary})`
            }} data-id="element-477" />
              <span className="text-[10px] tracking-[0.4em] uppercase font-serif" style={{
              color: sign.colors.accent
            }} data-id="element-478">
                Fragmentos del Archivo
              </span>
              <div className="h-px w-12" style={{
              background: `linear-gradient(90deg, ${sign.colors.primary}, transparent)`
            }} data-id="element-479" />
            </motion.div>
          </div>
          <h2 className="text-3xl md:text-5xl font-serif font-bold text-center text-white mb-5" data-id="element-480">
            Vista del Núcleo
          </h2>
          <p className="text-center max-w-2xl mx-auto text-base md:text-lg font-serif italic mb-14" style={{
          color: `${sign.colors.accent}D0`,
          textShadow: `0 0 20px ${sign.colors.primary}30`
        }} data-id="element-481">
            Fragmentos internos del archivo que explora la energía, patrones y
            esencia de cada entidad.
          </p>
        </AnimatedSection>

        {/* Carousel */}
        <div className="relative" data-id="element-482">
          {/* Desktop arrows */}
          <button onClick={() => goTo(active - 1)} aria-label="Anterior" className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full items-center justify-center transition-all duration-300 hover:scale-110" style={{
          backgroundColor: 'rgba(10,10,18,0.7)',
          border: `1px solid ${sign.colors.primary}60`,
          boxShadow: `0 0 20px ${sign.colors.primary}40`,
          color: sign.colors.accent,
          backdropFilter: 'blur(8px)'
        }} data-id="element-483">
            <ChevronLeftIcon size={22} data-id="element-484" />
          </button>
          <button onClick={() => goTo(active + 1)} aria-label="Siguiente" className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full items-center justify-center transition-all duration-300 hover:scale-110" style={{
          backgroundColor: 'rgba(10,10,18,0.7)',
          border: `1px solid ${sign.colors.primary}60`,
          boxShadow: `0 0 20px ${sign.colors.primary}40`,
          color: sign.colors.accent,
          backdropFilter: 'blur(8px)'
        }} data-id="element-485">
            <ChevronRightIcon size={22} data-id="element-486" />
          </button>

          {/* Scrollable track */}
          <div ref={scrollRef} className="flex gap-6 overflow-x-auto snap-x snap-mandatory pb-6 px-[10vw] md:px-[18vw] scroll-smooth no-scrollbar" style={{
          scrollbarWidth: 'none'
        }} data-id="element-487">
            {slides.map((slide, i) => {
            const isActive = i === active;
            return <motion.div key={i} className="snap-center flex-shrink-0 cursor-pointer" onClick={() => goTo(i)} animate={{
              scale: isActive ? 1 : 0.85,
              opacity: isActive ? 1 : 0.55
            }} transition={{
              type: 'spring',
              stiffness: 200,
              damping: 25
            }} whileHover={{
              scale: isActive ? 1.02 : 0.9
            }} data-id="element-488">
                  <Frame slide={slide} sign={sign} isActive={isActive} index={i} data-id="element-489" />
                </motion.div>;
          })}
          </div>

          {/* Dots indicator */}
          <div className="flex justify-center gap-2 mt-8" data-id="element-490">
            {slides.map((_, i) => <button key={i} onClick={() => goTo(i)} aria-label={`Ir al fragmento ${i + 1}`} className="transition-all duration-300" style={{
            width: i === active ? 28 : 8,
            height: 6,
            borderRadius: 3,
            backgroundColor: i === active ? sign.colors.primary : `${sign.colors.primary}40`,
            boxShadow: i === active ? `0 0 12px ${sign.colors.primary}` : 'none'
          }} data-id="element-491" />)}
          </div>
        </div>
      </div>

      <style data-id="element-492">{`
        .no-scrollbar::-webkit-scrollbar { display: none; }
      `}</style>
    </section>;
}
function Frame({
  slide,
  sign,
  isActive,
  index
}: {
  slide: {
    type: 'image' | 'placeholder';
    url?: string;
  };
  sign: Sign;
  isActive: boolean;
  index: number;
}) {
  return <div className="relative w-[78vw] sm:w-[60vw] md:w-[460px] lg:w-[520px] aspect-[3/4] rounded-2xl overflow-hidden group" style={{
    background: 'linear-gradient(160deg, rgba(10,10,18,0.95) 0%, rgba(5,5,10,1) 100%)',
    border: `1px solid ${sign.colors.primary}${isActive ? '90' : '40'}`,
    boxShadow: isActive ? `0 0 60px ${sign.colors.primary}50, 0 20px 60px rgba(0,0,0,0.6)` : `0 0 20px ${sign.colors.primary}20, 0 10px 30px rgba(0,0,0,0.4)`
  }} data-id="element-493">
      {slide.type === 'image' && slide.url ? <>
          <motion.img src={slide.url} alt={`Fragmento ${index + 1} del Núcleo ${sign.name}`} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" initial={{
        scale: 1.1
      }} animate={{
        scale: isActive ? 1 : 1.05
      }} transition={{
        duration: 1.2
      }} loading="lazy" data-id="element-494" />
          {/* Cinematic vignette */}
          <div className="absolute inset-0 pointer-events-none" style={{
        background: `radial-gradient(circle at 50% 50%, transparent 50%, rgba(0,0,0,0.5) 100%), linear-gradient(180deg, transparent 60%, rgba(5,5,8,0.85) 100%)`
      }} data-id="element-495" />
          {/* Sign color wash */}
          <div className="absolute inset-0 pointer-events-none opacity-20" style={{
        background: `linear-gradient(180deg, transparent 0%, ${sign.colors.primary}40 100%)`
      }} data-id="element-496" />
          {/* Hover glow */}
          <motion.div className="absolute inset-0 pointer-events-none rounded-2xl" animate={{
        opacity: isActive ? [0.4, 0.7, 0.4] : 0.3
      }} transition={{
        duration: 3,
        repeat: Infinity,
        ease: 'easeInOut'
      }} style={{
        boxShadow: `inset 0 0 80px ${sign.colors.primary}30`
      }} data-id="element-497" />
          {/* Index label */}
          <div className="absolute bottom-5 left-5 right-5 flex items-center justify-between" data-id="element-498">
            <span className="text-[10px] font-serif tracking-[0.35em] uppercase" style={{
          color: sign.colors.accent,
          textShadow: '0 2px 12px rgba(0,0,0,0.9)'
        }} data-id="element-499">
              Fragmento · {String(index + 1).padStart(2, '0')}
            </span>
            <span className="text-[10px] font-serif tracking-[0.25em] uppercase opacity-80" style={{
          color: '#F5F0E1',
          textShadow: '0 2px 12px rgba(0,0,0,0.9)'
        }} data-id="element-500">
              {sign.name}
            </span>
          </div>
        </> : <PlaceholderFrame sign={sign} index={index} isActive={isActive} data-id="element-501" />}
    </div>;
}
function PlaceholderFrame({
  sign,
  index,
  isActive
}: {
  sign: Sign;
  index: number;
  isActive: boolean;
}) {
  return <div className="relative w-full h-full flex flex-col items-center justify-center overflow-hidden" data-id="element-502">
      {/* Atmospheric radial glow */}
      <div className="absolute inset-0" style={{
      background: `radial-gradient(circle at 50% 40%, ${sign.colors.primary}30 0%, transparent 65%)`
    }} data-id="element-503" />

      {/* Floating particles */}
      {[...Array(10)].map((_, i) => <motion.div key={i} className="absolute w-[2px] h-[2px] rounded-full" style={{
      backgroundColor: sign.colors.primary,
      left: `${10 + i * 9}%`,
      top: `${20 + i % 4 * 20}%`,
      boxShadow: `0 0 8px ${sign.colors.primary}`
    }} animate={{
      y: [-10, -30, -10],
      opacity: [0.2, 0.7, 0.2]
    }} transition={{
      duration: 5 + i % 3,
      repeat: Infinity,
      ease: 'easeInOut',
      delay: i * 0.3
    }} data-id="element-504" />)}

      {/* Ornamental frame inset */}
      <div className="absolute inset-6 rounded-xl pointer-events-none" style={{
      border: `1px solid ${sign.colors.primary}40`,
      boxShadow: `inset 0 0 30px ${sign.colors.primary}15`
    }} data-id="element-505" />

      {/* Sacred sigil */}
      <motion.div animate={{
      rotate: 360
    }} transition={{
      duration: 70,
      repeat: Infinity,
      ease: 'linear'
    }} className="relative mb-8" data-id="element-506">
        <motion.div className="absolute inset-0 rounded-full" animate={{
        scale: isActive ? [1, 1.2, 1] : 1,
        opacity: isActive ? [0.4, 0.8, 0.4] : 0.5
      }} transition={{
        duration: 3.5,
        repeat: Infinity,
        ease: 'easeInOut'
      }} style={{
        boxShadow: `0 0 40px ${sign.colors.primary}80`
      }} data-id="element-507" />
        <svg width="88" height="88" viewBox="0 0 88 88" fill="none" data-id="element-508">
          <circle cx="44" cy="44" r="40" stroke={sign.colors.primary} strokeWidth="0.8" opacity="0.6" data-id="element-509" />
          <circle cx="44" cy="44" r="30" stroke={sign.colors.primary} strokeWidth="0.6" strokeDasharray="3 4" opacity="0.7" data-id="element-510" />
          {[0, 45, 90, 135].map(a => <line key={a} x1="44" y1="12" x2="44" y2="76" stroke={sign.colors.primary} strokeWidth="0.7" opacity="0.6" transform={`rotate(${a} 44 44)`} data-id="element-511" />)}
          <path d="M44 26 L58 44 L44 62 L30 44 Z" stroke={sign.colors.primary} strokeWidth="1" fill={sign.colors.primary} fillOpacity="0.15" data-id="element-512" />
          <circle cx="44" cy="44" r="4" fill={sign.colors.primary} data-id="element-513" />
        </svg>
      </motion.div>

      {/* Label */}
      <p className="text-[10px] font-serif tracking-[0.5em] uppercase mb-3 opacity-80" style={{
      color: sign.colors.accent
    }} data-id="element-514">
        Archivo
      </p>
      <p className="text-xl md:text-2xl font-serif italic px-6 text-center" style={{
      color: '#F5F0E1',
      textShadow: `0 0 20px ${sign.colors.primary}50`
    }} data-id="element-515">
        Archivo en expansión
      </p>
      <p className="mt-4 text-xs font-serif tracking-[0.3em] uppercase opacity-60" style={{
      color: sign.colors.accent
    }} data-id="element-516">
        {sign.name} · {String(index + 1).padStart(2, '0')}
      </p>
    </div>;
}